// PlayFirst Parent — Sponsor code redemption (school / teacher gift code).
// Inline card for the Settings page. If the parent is already on a sponsored
// seat, shows the sponsor's name and a passive "you're sponsored" badge.
// Otherwise: a small input + redeem button.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Loader2, Check, School } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function SponsorCodeCard() {
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [sponsored, setSponsored] = useState(null); // { sponsor_label } when on a seat
  const [loading, setLoading] = useState(true);

  // Light probe — check if /me carries an active sponsor_id.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, auth());
        if (r.data?.sponsor_id) {
          setSponsored({
            sponsor_id: r.data.sponsor_id,
            plan: r.data.parent_subscription_plan,
          });
        }
      } catch {
        /* ignore — endpoint may not return sponsor_id; silent fail is fine */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) {
      toast.error('Enter a code');
      return;
    }
    setRedeeming(true);
    try {
      const r = await axios.post(
        `${API}/parent-app/redeem-sponsor-code`,
        { code: cleaned },
        auth(),
      );
      toast.success(
        r.data?.sponsor_label
          ? `Sponsored by ${r.data.sponsor_label} — your PlayFirst Parent is active!`
          : 'Sponsor code applied!',
      );
      setSponsored({ sponsor_label: r.data?.sponsor_label });
      setCode('');
    } catch (err) {
      const msg = err?.response?.data?.detail || "We couldn't apply that code";
      toast.error(msg, { duration: 6000 });
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) return null;

  if (sponsored) {
    return (
      <div
        className="rounded-2xl bg-emerald-50/70 border border-emerald-200/70 p-4 flex items-start gap-3"
        data-testid="sponsor-active-card"
      >
        <div className="w-9 h-9 rounded-2xl bg-white text-emerald-700 flex items-center justify-center flex-shrink-0">
          <Check className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-emerald-900">
            You&apos;re on a sponsored seat — PlayFirst Parent is free for you.
          </p>
          <p className="text-xs text-emerald-800/80 mt-0.5">
            Your access is gifted by your sponsor. Thank them when you see them!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-5" data-testid="sponsor-redeem-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-2xl bg-rose-50 text-rose-700 flex items-center justify-center flex-shrink-0">
          <Gift className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-[#3a2f4f]">Got a sponsor code?</h3>
          <p className="text-xs text-stone-500 mt-0.5">
            Schools, preschools or pediatricians can pre-pay for families. If yours
            shared a code, enter it here for free access.
          </p>
        </div>
      </div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABC1234"
          className="flex-1 h-11 px-4 rounded-2xl border border-stone-200 bg-white font-mono tracking-wider uppercase focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
          data-testid="sponsor-redeem-input"
          maxLength={20}
        />
        <button
          type="submit"
          disabled={redeeming}
          className="h-11 px-5 rounded-2xl bg-[#7C6F9C] hover:bg-[#6a5d8a] text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="sponsor-redeem-submit"
        >
          {redeeming ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <School className="w-4 h-4" />
          )}
          {redeeming ? 'Redeeming…' : 'Redeem code'}
        </button>
      </form>
    </div>
  );
}
